// Module-private control-pane state singleton. Holds the live pane instance and
// the folder/input refs needed for rebuilds. Replaces viewer.pane — keeps
// pane-aware code inside src/ui/panel/ instead of leaking into the
// shared viewer schema.

// The pane handle is loose at the boundary; treat the instance as `unknown` /
// `Record<string, unknown>` and cast at consumers when a specific shape is
// needed. The folder refs are the exception — they have a known, bounded
// shape (PaneRefs).

import type { PaneRefs } from './types';

interface PaneState {
    instance: unknown;
    folders: Record<string, unknown>;
    refs: PaneRefs;
    monitors: Record<string, unknown>;
    activeSection?: string;
    lastActiveSection?: string;
    /** Sections with async work in flight — drives the amber tab-light pulse.
     *  Typed as Set<string> to avoid a cycle back to tabs.ts; callers cast. */
    busy: Set<string>;
    /** Full teardown for the live pane + its tab controller. Set by
     *  `initializePane`; called before a rebuild and on UI dispose so the rail
     *  listeners + intent subscriptions don't accumulate. */
    teardown?: (() => void) | null;
}

export const paneState: PaneState = {
    instance: null,
    folders: {},
    refs: {},
    monitors: {},
    busy: new Set<string>(),
    teardown: null,
};

export function resetPaneState(): void {
    paneState.instance = null;
    paneState.folders = {};
    paneState.refs = {};
    paneState.monitors = {};
    paneState.busy.clear();
    paneState.teardown = null;
    // activeSection / lastActiveSection deliberately preserved across rebuilds.
}
